<template>
    <div v-if="!isSupplier" class="card-body">
        <Button
            class="mr-2"
            color="teal"
            :disabled="!marketplaces.includes(integration.service)"
            @click="$refs.CreateFiltersFromExternalResourceModal.show()">
            <i class="icon-cloud-download2 mr-2"></i>
            {{ $t("buttons.import_from_external_resource") }}
        </Button>
        <router-link
            class="btn btn-link btn-md"
            :to="{
                name: 'CreateIntegrationFilterCard',
                params: {
                    categoryUuid: $route.params.categoryUuid
                }
            }">
            <i class="icon-plus3 mr-2"></i>
            {{ $t("buttons.create") }}
        </router-link>
    </div>
    <Table
        :handle="tableHandle"
        :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/filters`"
        :columns="filtersTable.columns"
        :has-checkbox="!isSupplier"
        :allowColumnSettings="true"
        @update:selectedValues="selectedFilters = $event"
        v-model:orders="filtersTable.orders"
    >
        <template v-if="!isSupplier" #actionDropdownItems>
            <DropdownItem @click="$refs.CalculateProductsCountModal.show(selectedFilters)">{{ $t('calculateProductsCountModal.showButton') }}</DropdownItem>
            <DropdownItem @click="$refs.EditFilterModal.show(selectedFilters)">{{ $t('buttons.edit') }}</DropdownItem>
            <DropdownItem @click="$refs.EditDefaultItemsModal.show(selectedFilters)">{{ $t('buttons.edit_default_items') }}</DropdownItem>
            <DropdownItem @click="$refs.DeleteFiltersModal.show(selectedFilters)">{{ $t('buttons.delete') }}</DropdownItem>
        </template>
        <template #td-id="{row}">
            <router-link :to="{ name: 'EditIntegrationFilterCard', params: { categoryUuid: row.integration_category_uuid, filterUuid: row.uuid }}">
                {{ row.id }}
            </router-link>
        </template>
        <template #td-source_id="{row}">
            <AppNullableCell>
                <template #default v-if="row.source_id">
                    {{ row.source_id }}
                </template>
            </AppNullableCell>
        </template>
        <template #[`td-name_${lang}`]="{row}" v-for="lang in integration.language_aliases" :key="`name_${lang}`">
            <AppNullableCell>
                <template #default v-if="row.names[lang]">
                    <span class="long-string" :title="row.names[lang]">{{ row.names[lang] }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-type="{ row }">
            {{ $t(`filter_types.${row.type}`) }}
            <br>
            <span v-if="row.type === 'number'" class="text-muted text-smaller">
                <span v-if="row.min_number_value !== null">
                    {{ $t('labels.min') }}: {{ row.min_number_value }}
                </span>
                <span v-if="row.max_number_value !== null">
                    <span v-if="row.min_number_value !== null"> - </span>
                    {{ $t('labels.max') }}: {{ row.max_number_value }}
                </span>
            </span>
        </template>
        <template #td-is_required="{ row }">
            {{ row.is_required ? $t('labels.yes') : $t('labels.no') }}
        </template>
        <template #td-allow_custom_values="{ row }">
            {{ row.allow_custom_values ? $t('labels.yes') : $t('labels.no') }}
        </template>
        <template #td-isMultiple="{ row }">
            {{ row.is_multiple ? $t('labels.yes') : $t('labels.no') }}
        </template>
        <template #td-measurement_unit="{ row }">
            <AppNullableCell>
                <template #default v-if="row.measurement_unit">
                    {{ row.measurement_unit }}
                </template>
            </AppNullableCell>
        </template>
        <template #td-ai_description="{ row }">
            <AppNullableCell>
                <template #default v-if="row.ai_description">
                    <span class="long-string" :title="row.ai_description">{{ row.ai_description }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-ai_use_in_product_description="{ row }">
            {{ row.ai_use_in_product_description ? $t('labels.yes') : $t('labels.no') }}
        </template>
        <template #td-default_items="{ row }">
            <AppNullableCell>
                <template #default v-if="row.default_items.length > 0">
                    <span class="long-string" :title="row.default_items.map(item => item.names[mainIntegrationLanguages]).join(', ')">{{ row.default_items.map(item => item.names[mainIntegrationLanguages]).join(', ') }}</span>
                </template>
            </AppNullableCell>
        </template>
    </Table>
    <CreateFiltersFromExternalResourceModal
        ref="CreateFiltersFromExternalResourceModal"
        @filters-created="fetchRows()"
        :integration="integration"
        :category="category"
    />
    <DeleteFiltersModal
        ref="DeleteFiltersModal"
        @filters:deleted="tableHandle.fetchRows()"
    />
    <EditDefaultItemsModal
        ref="EditDefaultItemsModal"
        :integration="integration"
        :category="category"
        @default-items:updated="fetchRows()"
    />
    <CalculateProductsCount ref="CalculateProductsCountModal" @filters:deleted="fetchRows()" />
    <EditFilterModal
        ref="EditFilterModal"
        @filters:edited="tableHandle.fetchRows()"
    />
</template>
<script>
import Button from "../../../../../../components/new/button/AppButton.vue";
import Table from "../../../../../../components/new/table/FetchTable.vue";
import { createTableHandle } from "../../../../../../components/new/table/tableHandle";
import AppNullableCell from "../../../../../components/tables/AppNullableCell.vue";
import DropdownItem from "../../../../../components/btnDropdown/DropdownItem.vue";
import CreateFiltersFromExternalResourceModal from "../../../modals/CreateFiltersFromExternalResourceModal.vue";
import DeleteFiltersModal from "../../../modals/DeleteFiltersModal.vue";
import EditDefaultItemsModal from "../modals/EditDefaultItemsModal.vue";
import CalculateProductsCount from "../modals/CalculateProductsCount.vue";
import EditFilterModal from "../modals/EditFilterModal.vue";

import { getCommonMarketplaces } from "../../../../../enums/CommonMarketplaceEnum";
import { IntegrationServiceEnum } from "../../../../../enums/IntegrationServiceEnum";

export default {
    name: "CategoryFiltersTable",
    components: {
        Button, Table, AppNullableCell, DropdownItem,
        CreateFiltersFromExternalResourceModal, DeleteFiltersModal,
        EditDefaultItemsModal, CalculateProductsCount, EditFilterModal
    },
    props: {
        integration: {
            type: Object,
            required: true
        },
        category: {
            type: Object,
            required: true
        }
    },
    emits: ['filters:updated'],
    data() {
        return {
            tableHandle: createTableHandle(),
            filtersTable: {
                columns: [],
                orders: []
            },
            selectedFilters: [],
            marketplaces: getCommonMarketplaces()
        }
    },
    created() {
        let languages = this.integration.language_aliases;

        let langFields = languages.map(lang => ({
            key: `name_${lang}`,
            name: this.$t(`table.titles.name`) + ` (${this.$t(`shortLang.${lang}`)})`,
            width: '200px',
            type: 'string',
            order: true,
            orderColumn: `names->${lang}`
        }));

        this.filtersTable.columns = [
            {key: "id", name: this.$t('table.titles.id'), width: "80px", type: "string", order: true},
            {key: "source_id", name: this.$t('table.titles.externalId'), width: "80px", type: "string", order: true},
            ...langFields,
            {key: "type", name: this.$t('table.titles.type'), width: "150px", type: "string", order: true},
            {key: "is_required", name: this.$t('labels.required'), width: "150px", type: "string", order: true},
            {key: "allow_custom_values", name: this.$t('labels.custom_values'), width: "150px", type: "string", order: true},
            {key: "isMultiple", name: this.$t('table.titles.is_multiple'), width: "150px", type: "string", order: true},
            {key: 'measurement_unit', name: this.$t('table.titles.measurement'), width: '150px', type: 'string', order: true, isShowed: true },
            {key: 'items_count', name: this.$t('labels.value'), width: '150px', type: 'string', order: true},
            {key: 'default_items', name: this.$t('table.titles.default_items'), width: '200px', type: 'string', order: false, isShowed: false },
            {key: 'ai_description', name: this.$t('table.titles.ai_description'), width: '300px', type: 'string', order: false, isShowed: false },
            {key: 'ai_use_in_product_description', name: this.$t('table.titles.ai_use_in_product_description'), width: '150px', type: 'string', order: false, isShowed: false }
        ]
    },
    methods: {
        fetchRows() {
            this.tableHandle.fetchRows({
                integration_category_uuids: [this.$route.params.categoryUuid],
                extra_fields: ['defaultItems', 'itemsCount']
            });
        }
    },
    computed: {
        isSupplier() {
            return this.integration.service === IntegrationServiceEnum.SUPPLIER
        },
        mainIntegrationLanguages() {
            return this.integration.language_aliases[0];
        }
    },
    mounted() {
        this.fetchRows();
    }
}
</script>
<style scoped>
.long-string {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}
.text-smaller {
    font-size: 12px;
    line-height: 18px;
}
</style>
